import React, { useEffect, useState, useRef } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { ClientPaymentInfoContainer, ClientPayWithContainer } from 'containers'
import { useDispatch, useSelector } from 'react-redux'
import {
  getProInvoice,
  getProInvoiceSummary,
  setClientPaymentInvoice,
  setClientChargesInvoice,
} from 'store/actions/invoices'
import { getClientPaymentSecret, getUserCards, getBankAccounts } from 'store/actions/payments'
import { ColorCircularProgress, ErrorModal } from 'components'
import { openSnackbar } from 'store/actions/common'

const useStyles = makeStyles({
  payNowContainer: {
    height: '100%',
    display: 'flex',
  },
})

const ClientInvoicePaymentPage = ({ match }) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const invoice = useSelector(state => state.invoices.invoice)
  const invoiceSummary = useSelector(state => state.invoices.invoiceSummary)
  const invoiceId = match.params.invoiceId
  const invoiceIsLoading = useSelector(state => state.invoices.invoiceIsLoading)
  const userCards = useSelector(state => state.payments.userCards)
  const bankAccounts = useSelector(state => state.payments.bankAccounts)
  const cardsAreLoading = useSelector(state => state.payments.cardsAreLoading)
  const bankAccountsAreLoading = useSelector(state => state.payments.bankAccountsAreLoading)
  const [paymentMethod, setPaymentMethod] = useState(null)
  const [showErrorModal, setShowErrorModal] = useState({ open: false, description: '', instruction: '' })
  const [inputAmount, setInputAmount] = useState(invoice.amountDue)
  const [paymentType, setPaymentType] = useState('full')
  const [clientChargesData, setClientChargesData] = useState(null)
  const [isChargesLoading, setIsChargesLoading] = useState(false)
  const lastChargesRequestRef = useRef({ uuid: null, paymentMethodId: null, amount: null, paymentType: null })
  const debounceTimerRef = useRef(null)

  useEffect(() => {
    setInputAmount(invoice.amountDue)
  }, [invoice])

  useEffect(() => {
    // Only execute when both requests are completed
    if (!cardsAreLoading && !bankAccountsAreLoading) {
      if (userCards.length || bankAccounts.length) {
        const defaultCard = userCards.find(card => card.isDefault)
        if (defaultCard) {
          setPaymentMethod(defaultCard)
          return
        }

        const readyBankAccounts = bankAccounts.filter(bank => bank.status === 'ready')
        const defaultBank = readyBankAccounts.find(bank => bank.is_default)
        if (defaultBank) {
          setPaymentMethod(defaultBank)
          return
        }

        if (userCards.length > 0) {
          setPaymentMethod(userCards[0])
        } else if (readyBankAccounts.length > 0) {
          setPaymentMethod(readyBankAccounts[0])
        }
      }
    }
  }, [invoice, userCards, bankAccounts, cardsAreLoading, bankAccountsAreLoading])

  useEffect(() => {
    // Reset charges data and ref when invoiceId changes
    setClientChargesData(null)
    lastChargesRequestRef.current = { uuid: null, paymentMethodId: null, amount: null, paymentType: null }
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    dispatch(getProInvoice(invoiceId, false, true))
    dispatch(getClientPaymentSecret())
    dispatch(getUserCards())
    dispatch(getBankAccounts())
  }, [invoiceId, dispatch])

  useEffect(() => {
    // Only proceed if invoice is loaded and matches current invoiceId from URL
    // This prevents using stale invoice data when account changes
    if (invoice.uuid && invoice.uuid === invoiceId && !invoiceIsLoading && paymentMethod) {
      // Extract payment method ID - cards use paymentMethodId, banks use payment_method_id
      const paymentMethodId = paymentMethod.paymentMethodId || paymentMethod.payment_method_id
      if (paymentMethodId) {
        const amount = paymentType === 'full' ? invoice.amountDue : inputAmount
        const amountString = !amount?.length ? '0' : amount.toString()

        // Check if we need to make a new request (avoid duplicate requests with same parameters)
        const lastRequest = lastChargesRequestRef.current
        if (
          lastRequest.uuid === invoice.uuid &&
          lastRequest.paymentMethodId === paymentMethodId &&
          lastRequest.amount === amountString &&
          lastRequest.paymentType === paymentType
        ) {
          return // Skip if same request parameters
        }

        // For inputAmount changes in 'partly' mode, use debounce
        const shouldDebounce =
          paymentType === 'partly' &&
          lastRequest.paymentType === 'partly' &&
          lastRequest.uuid === invoice.uuid &&
          lastRequest.paymentMethodId === paymentMethodId &&
          lastRequest.amount !== amountString

        const makeRequest = () => {
          // Update ref before making request
          lastChargesRequestRef.current = {
            uuid: invoice.uuid,
            paymentMethodId,
            amount: amountString,
            paymentType,
          }

          setIsChargesLoading(true)
          dispatch(setClientChargesInvoice(invoice.uuid, paymentMethodId, amountString))
            .then(data => {
              setClientChargesData(data)
              setIsChargesLoading(false)
            })
            .catch(e => {
              console.error('Error fetching client charges data:', e)
              setIsChargesLoading(false)
            })
        }

        if (shouldDebounce) {
          // Clear previous timer
          if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current)
          }
          // Set new timer for debounced request
          debounceTimerRef.current = setTimeout(() => {
            makeRequest()
          }, 500) // 500ms debounce
        } else {
          // Immediate request for other changes
          makeRequest()
        }
      }
    }

    // Cleanup timer on unmount or dependency change
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [invoice.uuid, invoiceId, invoiceIsLoading, paymentMethod, paymentType, inputAmount, dispatch])

  const onSubmit = paymentId => {
    const amount = paymentType === 'full' ? +invoice.amountDue : +inputAmount
    dispatch(setClientPaymentInvoice(invoice.uuid, paymentId, amount)).catch(e => {
      const errorData = e.response && e.response.data
      if (errorData && errorData.is_stripe_error) {
        const description = errorData.description
        const instruction = errorData.instructions
        setShowErrorModal({ open: true, description: description, instruction: instruction })
      } else
        dispatch(
          openSnackbar(
            'error',
            errorData?.detail || 'Error occurred. Please check the status of the invoice and try again'
          )
        )
    })
  }

  return (
    <div className={classes.payNowContainer}>
      {invoiceIsLoading ? (
        <ColorCircularProgress />
      ) : (
        <>
          <ClientPaymentInfoContainer
            invoice={invoice}
            invoiceSummary={invoiceSummary}
            amount={inputAmount}
            setAmount={setInputAmount}
            paymentType={paymentType}
            setPaymentType={setPaymentType}
            clientChargesData={clientChargesData}
          />
          <ClientPayWithContainer
            paymentMethod={paymentMethod}
            setPaymentMethod={setPaymentMethod}
            onSubmit={onSubmit}
            disablePayment={paymentType === 'partly' && !inputAmount}
          />
        </>
      )}
      <ErrorModal
        variant="primary"
        title="Your payment failed"
        message={`${showErrorModal.description} ${showErrorModal.instruction}`}
        cancelationButtonText="select another payment method"
        isOpen={showErrorModal.open}
        onClose={() => setShowErrorModal({ description: '', instruction: '', open: false })}
      />
    </div>
  )
}

export default ClientInvoicePaymentPage
